import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { de } from 'date-fns/locale';

import EditAppointment from '../../components/DailySchedulePage/EditAppointment';
import { useTenant } from '../../contexts/TenantContext';
import {
  buildActivityText,
  calcEndTime,
  createAdminAppointment,
  computeAvailableSlots,
  formatLocalDate,
  loadAdminBaseData,
  loadAvailabilityDataForDate,
  loadAppointmentsForRange,
  timeToMinutes,
} from '../../utils/adminAppointments';

const calendarViews = [
  { value: 'day', label: 'Tag' },
  { value: 'week', label: 'Woche' },
  { value: 'month', label: 'Monat' },
];

const parseDateString = (dateString) => new Date(`${dateString}T00:00:00`);

const getCalendarRange = (view, dateString) => {
  const date = parseDateString(dateString);
  if (view === 'month') {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    return {
      start: startOfWeek(monthStart, { weekStartsOn: 1 }),
      end: endOfWeek(monthEnd, { weekStartsOn: 1 }),
    };
  }
  if (view === 'week') {
    return {
      start: startOfWeek(date, { weekStartsOn: 1 }),
      end: endOfWeek(date, { weekStartsOn: 1 }),
    };
  }
  return { start: date, end: date };
};

const emptyForm = (date) => ({
  date,
  startTime: '09:00',
  duration: '',
  employeeId: '',
  appointmentTypeId: '',
  customerName: '',
  customerEmail: '',
  customerPhoneNumber: '',
  notes: '',
});

const DailySchedulePage = () => {
  const { tenantId } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(formatLocalDate());
  const [calendarView, setCalendarView] = useState('week');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm(formatLocalDate()));
  const [slotData, setSlotData] = useState({ availabilities: [], absences: [], appointments: [] });
  const [slotLoading, setSlotLoading] = useState(false);
  const [editAppointmentId, setEditAppointmentId] = useState(null);

  const employeeMap = useMemo(
    () => Object.fromEntries(employees.map((employee) => [employee.id, employee])),
    [employees]
  );

  const appointmentTypeMap = useMemo(
    () => Object.fromEntries(appointmentTypes.map((type) => [type.id, type])),
    [appointmentTypes]
  );

  const calendarRange = useMemo(
    () => getCalendarRange(calendarView, selectedDate),
    [calendarView, selectedDate]
  );

  const calendarDays = useMemo(
    () => eachDayOfInterval(calendarRange),
    [calendarRange]
  );

  const calendarRangeLabel = useMemo(() => {
    if (calendarView === 'day') {
      return format(parseDateString(selectedDate), 'dd. MMMM yyyy', { locale: de });
    }
    if (calendarView === 'month') {
      return format(parseDateString(selectedDate), 'MMMM yyyy', { locale: de });
    }
    return `${format(calendarRange.start, 'dd. MMM', { locale: de })} - ${format(calendarRange.end, 'dd. MMM yyyy', { locale: de })}`;
  }, [calendarRange, calendarView, selectedDate]);

  const appointmentsForSelectedDate = useMemo(
    () =>
      appointments.filter((appointment) => {
        if (appointment.date !== selectedDate) return false;
        if (selectedEmployeeId && appointment.employeeId !== selectedEmployeeId) return false;
        return true;
      }),
    [appointments, selectedDate, selectedEmployeeId]
  );

  const activeAppointments = useMemo(
    () =>
      appointmentsForSelectedDate
        .filter((appointment) => appointment.canceled !== true)
        .sort((a, b) => {
          const timeDiff = timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
          if (timeDiff !== 0) return timeDiff;
          return (employeeMap[a.employeeId]?.name || '').localeCompare(employeeMap[b.employeeId]?.name || '');
        }),
    [appointmentsForSelectedDate, employeeMap]
  );

  const canceledAppointments = appointmentsForSelectedDate.filter((appointment) => appointment.canceled === true);

  const employeeStats = useMemo(() => {
    const stats = employees.map((employee) => ({
      employee,
      count: activeAppointments.filter((appointment) => appointment.employeeId === employee.id).length,
    }));
    return stats.sort((a, b) => b.count - a.count || a.employee.name.localeCompare(b.employee.name));
  }, [activeAppointments, employees]);

  const availableSlots = useMemo(
    () =>
      computeAvailableSlots({
        date: form.date,
        appointmentTypeId: form.appointmentTypeId,
        employeeId: form.employeeId,
        appointmentTypes,
        employees,
        availabilities: slotData.availabilities,
        absences: slotData.absences,
        appointments: slotData.appointments,
      }),
    [appointmentTypes, employees, form.appointmentTypeId, form.date, form.employeeId, slotData]
  );

  const slotHint = useMemo(() => {
    if (!form.appointmentTypeId) return null;
    if (slotData.availabilities.length === 0) {
      return 'Es sind keine Verfügbarkeiten hinterlegt.';
    }
    const selectedType = appointmentTypes.find((type) => type.id === form.appointmentTypeId);
    if (selectedType?.assignedEmployees?.length === 0 && !form.employeeId) {
      return 'Diese Terminart ist keinem Mitarbeiter zugeordnet. Es werden trotzdem alle Mitarbeiter geprüft.';
    }
    return null;
  }, [appointmentTypes, form.appointmentTypeId, form.employeeId, slotData.availabilities.length]);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const rangeStart = format(calendarRange.start, 'yyyy-MM-dd');
      const rangeEnd = format(calendarRange.end, 'yyyy-MM-dd');
      const [{ employees: loadedEmployees, appointmentTypes: loadedTypes }, loadedAppointments] =
        await Promise.all([
          loadAdminBaseData(tenantId),
          loadAppointmentsForRange(tenantId, rangeStart, rangeEnd),
        ]);
      setEmployees(loadedEmployees);
      setAppointmentTypes(loadedTypes);
      setAppointments(loadedAppointments);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Tagesübersicht konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [calendarRange, tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') !== '1') return;

    setForm(emptyForm(selectedDate));
    setSlotData({ availabilities: [], absences: [], appointments: [] });
    setFormError(null);
    setDialogOpen(true);
    navigate('/schedule', { replace: true });
  }, [location.search, navigate, selectedDate]);

  useEffect(() => {
    if (!dialogOpen || !tenantId || !form.date) return;

    const loadSlots = async () => {
      setSlotLoading(true);
      try {
        const data = await loadAvailabilityDataForDate(tenantId, form.date);
        setSlotData(data);
      } catch (err) {
        console.error(err);
        setFormError('Freie Zeiten konnten nicht geladen werden.');
      } finally {
        setSlotLoading(false);
      }
    };

    loadSlots();
  }, [dialogOpen, tenantId, form.date]);

  const updateForm = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'appointmentTypeId') {
        const type = appointmentTypes.find((item) => item.id === value);
        next.duration = type?.duration || '';
      }
      return next;
    });
  };

  const openCreateDialog = () => {
    setForm(emptyForm(selectedDate));
    setSlotData({ availabilities: [], absences: [], appointments: [] });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSelectSlot = (slot) => {
    setForm((prev) => ({
      ...prev,
      employeeId: slot.employeeId,
      startTime: slot.startTime,
      duration: slot.duration,
    }));
  };

  const handleCreateAppointment = async () => {
    if (
      !form.date ||
      !form.startTime ||
      !form.duration ||
      !form.employeeId ||
      !form.appointmentTypeId ||
      !form.customerName.trim()
    ) {
      setFormError('Datum, Uhrzeit, Mitarbeiter, Terminart, Dauer und Kundenname sind erforderlich.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const conflictAppointments = form.date === selectedDate ? appointments : slotData.appointments;
      await createAdminAppointment(tenantId, form, appointmentTypes, conflictAppointments);
      setDialogOpen(false);
      if (form.date !== selectedDate) {
        setSelectedDate(form.date);
        return;
      }
      await loadData();
    } catch (err) {
      console.error(err);
      setFormError(err.message || 'Termin konnte nicht angelegt werden.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviousRange = () => {
    const current = parseDateString(selectedDate);
    if (calendarView === 'month') {
      setSelectedDate(format(subMonths(current, 1), 'yyyy-MM-dd'));
      return;
    }
    if (calendarView === 'week') {
      setSelectedDate(format(subWeeks(current, 1), 'yyyy-MM-dd'));
      return;
    }
    setSelectedDate(format(addDays(current, -1), 'yyyy-MM-dd'));
  };

  const handleNextRange = () => {
    const current = parseDateString(selectedDate);
    if (calendarView === 'month') {
      setSelectedDate(format(addMonths(current, 1), 'yyyy-MM-dd'));
      return;
    }
    if (calendarView === 'week') {
      setSelectedDate(format(addWeeks(current, 1), 'yyyy-MM-dd'));
      return;
    }
    setSelectedDate(format(addDays(current, 1), 'yyyy-MM-dd'));
  };

  const getAppointmentsForDay = (day) =>
    appointments
      .filter((appointment) => {
        if (appointment.date !== format(day, 'yyyy-MM-dd')) return false;
        if (selectedEmployeeId && appointment.employeeId !== selectedEmployeeId) return false;
        return true;
      })
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: '32px auto' }} />;
  }

  return (
    <Box sx={{ bgcolor: '#f6f8fb', minHeight: '100vh', p: { xs: 2, md: 4 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#18212f' }}>
            Tagesübersicht
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {format(new Date(selectedDate), 'EEEE, dd. MMMM yyyy', { locale: de })}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="Datum"
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
            <InputLabel>Mitarbeiter</InputLabel>
            <Select
              label="Mitarbeiter"
              value={selectedEmployeeId}
              onChange={(event) => setSelectedEmployeeId(event.target.value)}
            >
              <MenuItem value="">
                <em>Alle Mitarbeiter</em>
              </MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>{employee.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Termin anlegen
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="outlined" onClick={handlePreviousRange}>
              <ChevronLeftIcon fontSize="small" />
            </Button>
            <Button size="small" variant="outlined" onClick={() => setSelectedDate(formatLocalDate())}>
              Heute
            </Button>
            <Button size="small" variant="outlined" onClick={handleNextRange}>
              <ChevronRightIcon fontSize="small" />
            </Button>
            <Typography variant="h6" sx={{ ml: 1 }}>
              {calendarRangeLabel}
            </Typography>
          </Stack>
          <ButtonGroup size="small" variant="outlined">
            {calendarViews.map((view) => (
              <Button
                key={view.value}
                variant={calendarView === view.value ? 'contained' : 'outlined'}
                onClick={() => setCalendarView(view.value)}
              >
                {view.label}
              </Button>
            ))}
          </ButtonGroup>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: calendarView === 'day' ? '1fr' : 'repeat(7, minmax(0, 1fr))',
            },
            borderLeft: { md: '1px solid' },
            borderColor: 'divider',
          }}
        >
          {calendarDays.map((day) => {
            const dayString = format(day, 'yyyy-MM-dd');
            const dayAppointments = getAppointmentsForDay(day);
            const isSelected = dayString === selectedDate;
            const isOutsideMonth = calendarView === 'month' && day.getMonth() !== parseDateString(selectedDate).getMonth();

            return (
              <Box
                key={dayString}
                onClick={() => setSelectedDate(dayString)}
                sx={{
                  minHeight: calendarView === 'month' ? 154 : 260,
                  p: 1,
                  cursor: 'pointer',
                  bgcolor: isSelected ? '#eef6ff' : isOutsideMonth ? '#f8fafc' : 'background.paper',
                  borderRight: { md: '1px solid' },
                  borderBottom: '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  outline: isSelected ? '2px solid' : 'none',
                  outlineColor: 'primary.main',
                  outlineOffset: '-2px',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {format(day, 'EEE', { locale: de })}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isOutsideMonth ? 'text.disabled' : 'text.primary' }}>
                      {format(day, 'dd.MM.', { locale: de })}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={dayAppointments.filter((appointment) => appointment.canceled !== true).length}
                    color={dayAppointments.some((appointment) => appointment.canceled !== true) ? 'primary' : 'default'}
                  />
                </Stack>

                <Stack spacing={0.75}>
                  {dayAppointments.slice(0, calendarView === 'month' ? 4 : 12).map((appointment) => {
                    const type = appointmentTypeMap[appointment.appointmentTypeId];
                    const employee = employeeMap[appointment.employeeId];
                    const canceled = appointment.canceled === true;
                    return (
                      <Box
                        key={appointment.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedDate(dayString);
                          setEditAppointmentId(appointment.id);
                        }}
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor: canceled ? '#fff7e6' : '#e8f1ff',
                          border: '1px solid',
                          borderColor: canceled ? 'warning.light' : 'primary.light',
                          opacity: canceled ? 0.72 : 1,
                        }}
                      >
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2 }}>
                          {appointment.startTime} {appointment.customerName || 'Ohne Namen'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {type?.name || 'Termin'} · {employee?.name || 'Unbekannt'}
                        </Typography>
                      </Box>
                    );
                  })}
                  {dayAppointments.length > (calendarView === 'month' ? 4 : 12) && (
                    <Typography variant="caption" color="text.secondary">
                      +{dayAppointments.length - (calendarView === 'month' ? 4 : 12)} weitere
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Zeit</TableCell>
                  <TableCell>Kunde</TableCell>
                  <TableCell>Terminart</TableCell>
                  <TableCell>Mitarbeiter</TableCell>
                  <TableCell>Kontakt</TableCell>
                  <TableCell align="right">Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeAppointments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Alert severity="info">Für diesen Tag sind keine aktiven Termine eingetragen.</Alert>
                    </TableCell>
                  </TableRow>
                )}
                {activeAppointments.map((appointment) => {
                  const type = appointmentTypeMap[appointment.appointmentTypeId];
                  const employee = employeeMap[appointment.employeeId];
                  return (
                    <TableRow key={appointment.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                        {appointment.startTime} - {calcEndTime(appointment.startTime, appointment.duration)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {appointment.customerName || 'Ohne Namen'}
                        </Typography>
                        {appointment.bookingSource === 'admin' && (
                          <Chip label="intern" size="small" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>{type?.name || 'Unbekannt'}</TableCell>
                      <TableCell>{employee?.name || 'Unbekannt'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{appointment.customerPhoneNumber || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {appointment.customerEmail || ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => setEditAppointmentId(appointment.id)}>
                          Bearbeiten
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Auslastung</Typography>
              <Stack spacing={1}>
                {employeeStats.map(({ employee, count }) => (
                  <Stack key={employee.id} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{employee.name}</Typography>
                    <Chip label={`${count} Termine`} size="small" color={count > 0 ? 'primary' : 'default'} />
                  </Stack>
                ))}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Storniert am Tag</Typography>
              {canceledAppointments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Keine Stornos für dieses Datum.</Typography>
              ) : (
                <Stack spacing={1}>
                  {canceledAppointments.map((appointment) => (
                    <Alert key={appointment.id} severity="warning" variant="outlined">
                      {buildActivityText({
                        type: 'appointment_canceled',
                        customerName: appointment.customerName,
                        appointmentDate: appointment.date,
                        startTime: appointment.startTime,
                      })}
                    </Alert>
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {editAppointmentId && (
        <Dialog open fullWidth maxWidth="md" onClose={() => setEditAppointmentId(null)}>
          <EditAppointment
            appointmentId={editAppointmentId}
            onClose={() => setEditAppointmentId(null)}
            onAppointmentsChanged={loadData}
          />
        </Dialog>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Telefonischen Termin anlegen</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Datum"
                type="date"
                fullWidth
                value={form.date}
                onChange={(event) => updateForm('date', event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Startzeit"
                type="time"
                fullWidth
                value={form.startTime}
                onChange={(event) => updateForm('startTime', event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Dauer"
                type="number"
                fullWidth
                value={form.duration}
                onChange={(event) => updateForm('duration', event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Mitarbeiter</InputLabel>
                <Select
                  label="Mitarbeiter"
                  value={form.employeeId}
                  onChange={(event) => updateForm('employeeId', event.target.value)}
                >
                  <MenuItem value="">
                    <em>Alle passenden Mitarbeiter</em>
                  </MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>{employee.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Terminart</InputLabel>
                <Select
                  label="Terminart"
                  value={form.appointmentTypeId}
                  onChange={(event) => updateForm('appointmentTypeId', event.target.value)}
                >
                  {appointmentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fbfcfe' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Freie Zeiten
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Klick auf einen Slot übernimmt Mitarbeiter und Uhrzeit.
                    </Typography>
                  </Box>
                  {form.appointmentTypeId && (
                    <Chip
                      label={`${availableSlots.length} freie Slots`}
                      color={availableSlots.length > 0 ? 'primary' : 'default'}
                      size="small"
                    />
                  )}
                </Stack>

                {!form.appointmentTypeId ? (
                  <Alert severity="info">Bitte zuerst eine Terminart wählen, damit die Dauer bekannt ist.</Alert>
                ) : slotLoading ? (
                  <CircularProgress size={24} />
                ) : availableSlots.length === 0 ? (
                  <Stack spacing={1}>
                    <Alert severity="warning">
                      Keine freien Slots gefunden. Du kannst Uhrzeit und Mitarbeiter unten trotzdem manuell setzen.
                    </Alert>
                    {slotHint && <Alert severity="info">{slotHint}</Alert>}
                  </Stack>
                ) : (
                  <Grid container spacing={1}>
                    {availableSlots.slice(0, 36).map((slot) => {
                      const selected = form.employeeId === slot.employeeId && form.startTime === slot.startTime;
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`${slot.employeeId}-${slot.startTime}`}>
                          <Button
                            fullWidth
                            variant={selected ? 'contained' : 'outlined'}
                            onClick={() => handleSelectSlot(slot)}
                            sx={{
                              justifyContent: 'space-between',
                              textTransform: 'none',
                              minHeight: 48,
                            }}
                          >
                            <span>{slot.startTime} - {slot.endTime}</span>
                            <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                              {slot.employeeName}
                            </Typography>
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
                {availableSlots.length > 36 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Es werden die ersten 36 freien Slots angezeigt. Filtere nach Mitarbeiter für eine engere Auswahl.
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Kundenname"
                fullWidth
                value={form.customerName}
                onChange={(event) => updateForm('customerName', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefon"
                type="tel"
                fullWidth
                value={form.customerPhoneNumber}
                onChange={(event) => updateForm('customerPhoneNumber', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="E-Mail"
                type="email"
                fullWidth
                value={form.customerEmail}
                onChange={(event) => updateForm('customerEmail', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notiz"
                fullWidth
                multiline
                minRows={3}
                value={form.notes}
                onChange={(event) => updateForm('notes', event.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreateAppointment} disabled={saving}>
            {saving ? 'Speichert...' : 'Termin speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailySchedulePage;
